// https://github.com/tldraw/tldraw/blob/c1b84bf2468caeb0c6e502f621b19dffe3aa8aba/packages/store/src/lib/migrate.ts#L0-L1
import { isRecord } from "./BaseRecord";
/** @public */ export function defineMigrations(opts) {
    const { currentVersion, firstVersion, migrators = {}, subTypeKey, subTypeMigrations } = opts;
    // Some basic guards against impossible version combinations, some of which will be caught by TypeScript
    if (typeof currentVersion === "number" && typeof firstVersion === "number") {
        if (currentVersion === firstVersion) {
            throw Error(`Current version is equal to initial version.`);
        } else if (currentVersion < firstVersion) {
            throw Error(`Current version is lower than initial version.`);
        }
    }
    return {
        firstVersion: firstVersion ?? 0,
        currentVersion: currentVersion ?? 0,
        migrators,
        subTypeKey,
        subTypeMigrations
    };
}
/** @public */ export var MigrationFailureReason = /*#__PURE__*/ function(MigrationFailureReason) {
    MigrationFailureReason["IncompatibleSubtype"] = "incompatible-subtype";
    MigrationFailureReason["UnknownType"] = "unknown-type";
    MigrationFailureReason["TargetVersionTooNew"] = "target-version-too-new";
    MigrationFailureReason["TargetVersionTooOld"] = "target-version-too-old";
    MigrationFailureReason["MigrationError"] = "migration-error";
    MigrationFailureReason["UnrecognizedSubtype"] = "unrecognized-subtype";
    return MigrationFailureReason;
}({});
/** @public */ export function getRecordVersion(record, serializedSchema) {
    const persistedType = serializedSchema.recordVersions[record.typeName];
    if (!persistedType) {
        return {
            rootVersion: 0
        };
    }
    if ("subTypeKey" in persistedType) {
        const subType = record[persistedType.subTypeKey];
        const subTypeVersion = persistedType.subTypeVersions[subType];
        return {
            rootVersion: persistedType.version,
            subTypeVersion
        };
    }
    return {
        rootVersion: persistedType.version
    };
}
/** @public */ export function compareRecordVersions(a, b) {
    if (a.rootVersion > b.rootVersion) {
        return 1;
    }
    if (a.rootVersion < b.rootVersion) {
        return -1;
    }
    if (a.subTypeVersion != null && b.subTypeVersion != null) {
        if (a.subTypeVersion > b.subTypeVersion) {
            return 1;
        }
        if (a.subTypeVersion < b.subTypeVersion) {
            return -1;
        }
    }
    return 0;
}
/** @public */ export function migrateRecord({ record, migrations, fromVersion, toVersion }) {
    let currentVersion = fromVersion;
    if (!isRecord(record)) throw new Error("[migrateRecord] object is not a record");
    const { typeName, id, ...others } = record;
    // Hack to migrate products by id
    let recordWithoutMeta = others;
    if (typeName === "paywall_product") {
        recordWithoutMeta = {
            ...others,
            id,
            typeName
        };
    }
    while(currentVersion < toVersion){
        const nextVersion = currentVersion + 1;
        const migrator = migrations.migrators[nextVersion];
        if (!migrator) {
            return {
                type: "error",
                reason: "target-version-too-new"
            };
        }
        recordWithoutMeta = migrator.up(recordWithoutMeta);
        currentVersion = nextVersion;
    }
    while(currentVersion > toVersion){
        const nextVersion = currentVersion - 1;
        const migrator = migrations.migrators[currentVersion];
        if (!migrator) {
            return {
                type: "error",
                reason: "target-version-too-old"
            };
        }
        recordWithoutMeta = migrator.down(recordWithoutMeta);
        currentVersion = nextVersion;
    }
    return {
        type: "success",
        value: {
            ...recordWithoutMeta,
            id,
            typeName
        }
    };
}
/** @public */ export function migrate({ value, migrations, fromVersion, toVersion }) {
    let currentVersion = fromVersion;
    while(currentVersion < toVersion){
        const nextVersion = currentVersion + 1;
        const migrator = migrations.migrators[nextVersion];
        if (!migrator) {
            return {
                type: "error",
                reason: "target-version-too-new"
            };
        }
        value = migrator.up(value);
        currentVersion = nextVersion;
    }
    while(currentVersion > toVersion){
        const nextVersion = currentVersion - 1;
        const migrator = migrations.migrators[currentVersion];
        if (!migrator) {
            return {
                type: "error",
                reason: "target-version-too-old"
            };
        }
        value = migrator.down(value);
        currentVersion = nextVersion;
    }
    return {
        type: "success",
        value: value
    };
}
